import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Text } from '@fluentui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { EditItem, FIELD_LABEL_LAYOUT, useFetch } from '@moko/ui';
import { Field } from '@fluentui/react-components';
import AppContext from '../AppContext';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  createPricingRate,
  getPricingItems,
  getPricingRate,
  getTimeServices,
  IPricingItem,
  updatePricingRate,
} from '../../services/pricingService';
import {
  getBillingClasses,
  getWorkTypes,
  type IBillingClass,
  type IWorkType,
} from '../../services/settingsService';
import { getCurrencies, ICatalogCurrency } from '../../services/tenantService';
import {
  conditionKey,
  conditionOptions,
  pricingRateBody,
  pricingRateErrorMessage,
  rateTimeServiceId,
} from './pricingRateBody';
import { pricingRatesPath } from './pricingSettings';

const currencyLabel = (code: string) => {
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'currency' }).of(code);
    return name ? `${name} (${code})` : code;
  } catch {
    return code;
  }
};

const PricingRateForm = () => {
  const isSaved = useRef(false);
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const pricingPath = pricingRatesPath;
  const rateId = Number.parseInt(query.get('id') ?? '');
  const isEdit = !Number.isNaN(rateId) && rateId > 0;
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const context = useContext(AppContext);
  const title = isEdit ? 'Edit pricing rate' : 'Add pricing rate';
  const [items, setItems] = useState<IPricingItem[]>([]);
  const [pricingItemId, setPricingItemId] = useState<number>();

  useEffect(() => {
    context.setSelectedTab('Global settings');
  }, []);

  const currencyFetch = useFetch<ICatalogCurrency[]>({
    queryKey: ['currencies'],
    queryFn: getCurrencies,
  });
  const workTypeFetch = useFetch<IWorkType[]>({
    queryKey: ['workTypes'],
    queryFn: getWorkTypes,
    onError: (error) =>
      setErrorMessage(error.message || 'Failed to load work types.'),
  });
  const billingClassFetch = useFetch<IBillingClass[]>({
    queryKey: ['billingClasses'],
    queryFn: getBillingClasses,
    onError: (error) =>
      setErrorMessage(error.message || 'Failed to load billing classes.'),
  });
  const timeServiceFetch = useFetch({
    queryKey: ['timeServices', 500, 1],
    queryFn: (abortController) => getTimeServices(abortController, 500, 1),
    onError: (error) =>
      setErrorMessage(error.message || 'Failed to load time services.'),
  });

  const currencyOptions = useMemo(
    () =>
      (currencyFetch.data ?? []).map((currency) => ({
        key: currency.code,
        value: currency.code,
        optionLabel: currencyLabel(currency.code),
      })),
    [currencyFetch.data]
  );
  const selectedItem = items.find((item) => item.id === pricingItemId);
  const percentage = selectedItem?.calculationMethod === 'Percentage';
  const fixedTimeServiceId = selectedItem?.timeServiceId;
  const fixedTimeService = timeServiceFetch.data?.items.find(
    (service) => service.id === fixedTimeServiceId
  );

  useEffect(() => {
    const abortController = new AbortController();
    getPricingItems(abortController, 500, 1)
      .then((page) => setItems(page.items))
      .catch((error: { message?: string }) => {
        if (abortController.signal.aborted) return;
        setErrorMessage(error.message ?? 'Failed to load pricing items.');
      });
    return () => abortController.abort();
  }, [setErrorMessage]);

  const saveRate = async (
    abortController: AbortController,
    item: Record<string, unknown>,
    id?: number
  ) => {
    const pricingItem = items.find(
      (row) => row.id === Number(item.pricingItemId)
    );
    const result = pricingRateBody({
      ...item,
      timeServiceId: rateTimeServiceId(pricingItem, item.timeServiceId),
    });
    if (!result.ok) {
      setErrorMessage(result.error);
      throw new Error(result.error);
    }
    try {
      if (id == null) {
        await createPricingRate(abortController, result.body);
        setSuccessMessage('Pricing rate created.');
      } else {
        await updatePricingRate(abortController, id, result.body);
        setSuccessMessage(`Pricing rate ${id} updated.`);
      }
      navigate(pricingPath);
    } catch (error) {
      setErrorMessage(pricingRateErrorMessage(error));
      throw error;
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <Text variant="xLarge">{title}</Text>
      <EditItem
        saveDisabled={!selectedItem}
        renderAfterField={(name) =>
          name === 'pricingItemId' && fixedTimeServiceId != null ? (
            <Field
              label="Turnaround"
              orientation="horizontal"
              style={FIELD_LABEL_LAYOUT}
              hint="Turnaround is defined by the pricing item. Select a different pricing item to change it."
            >
              <span style={{ alignSelf: 'center' }}>
                {fixedTimeService?.name ||
                  fixedTimeService?.code ||
                  String(fixedTimeServiceId)}
              </span>
            </Field>
          ) : null
        }
        getAction={async (abortController, id) => {
          const rate = await getPricingRate(abortController, id);
          setPricingItemId(rate.pricingItemId);
          return {
            ...rate,
            amount_Unit: rate.currencyCode,
            workTypeId: conditionKey(rate.workTypeId),
            billingClassId: conditionKey(rate.billingClassId),
            timeServiceId: conditionKey(rate.timeServiceId),
            isDiscountable: rate.isDiscountable === true,
          };
        }}
        newAction={(abortController, item) => saveRate(abortController, item)}
        editAction={(abortController, id, item) =>
          saveRate(abortController, item, id)
        }
        afterChange={(item) => {
          const id = Number(item.pricingItemId);
          setPricingItemId(Number.isInteger(id) && id > 0 ? id : undefined);
        }}
        isSaved={isSaved}
        back={pricingPath}
        confirmOnCancel={false}
        onCancel={() => navigate(pricingPath)}
        defaultValues={
          isEdit
            ? undefined
            : JSON.stringify({
                workTypeId: 'none',
                billingClassId: 'none',
                timeServiceId: 'none',
                isDiscountable: false,
              })
        }
        metadata={{
          fields: [
            {
              name: 'pricingItemId',
              fieldType: 'Lookup',
              label: 'Pricing item',
              lookupList: 'PricingItems',
              mandatory: true,
            },
            {
              name: 'workTypeId',
              fieldType: 'Lookup',
              label: 'Work type',
              lookupList: 'WorkTypes',
            },
            {
              name: 'billingClassId',
              fieldType: 'Lookup',
              label: 'Billing class',
              lookupList: 'BillingClasses',
            },
            {
              name: 'timeServiceId',
              fieldType: 'Lookup',
              label: 'Turnaround',
              lookupList: 'TimeServices',
              effect: fixedTimeServiceId != null ? 'Hide' : undefined,
            },
            {
              name: 'isDiscountable',
              fieldType: 'Checkbox',
              label: 'Discountable',
            },
            percentage
              ? {
                  name: 'amount',
                  fieldType: 'Number',
                  label: 'Amount (%)',
                  hint: 'Enter 25 for 25%.',
                  mandatory: true,
                }
              : {
                  name: 'amount',
                  fieldType: 'NumberWithUnit',
                  label: 'Amount',
                  lookupList: 'Currencies',
                  mandatory: true,
                },
            ...(percentage
              ? [
                  {
                    name: 'amount_Unit',
                    fieldType: 'Lookup',
                    label: 'Currency',
                    lookupList: 'Currencies',
                    mandatory: true,
                  },
                ]
              : []),
            {
              name: 'effectiveFrom',
              fieldType: 'Date',
              label: 'Effective from',
              mandatory: true,
            },
            {
              name: 'effectiveTo',
              fieldType: 'Date',
              label: 'Effective to',
            },
          ],
          lookups: [
            {
              name: 'PricingItems',
              values: items.map((item) => ({
                key: String(item.id),
                value: item.name || item.code,
              })),
            },
            {
              name: 'WorkTypes',
              values: conditionOptions(workTypeFetch.data ?? []),
            },
            {
              name: 'BillingClasses',
              values: conditionOptions(billingClassFetch.data ?? []),
            },
            {
              name: 'TimeServices',
              values: conditionOptions(
                (timeServiceFetch.data?.items ?? []).filter(
                  (service) => service.isActive
                )
              ),
            },
            { name: 'Currencies', values: currencyOptions },
          ],
          validations: [
            {
              field: 'pricingItemId',
              rules: [
                { rule: 'NE', errorMessage: 'Pricing item is required.' },
              ],
            },
            {
              field: 'amount',
              rules: [
                { rule: 'NE', errorMessage: 'Amount is required.' },
                {
                  rule: 'RGX^(?:[1-9]\\d*(?:\\.\\d+)?|0+\\.\\d*[1-9]\\d*)$',
                  errorMessage: 'Amount must be greater than 0.',
                },
              ],
            },
            {
              field: 'amount_Unit',
              rules: [{ rule: 'NE', errorMessage: 'Currency is required.' }],
            },
            {
              field: 'effectiveFrom',
              rules: [
                { rule: 'NE', errorMessage: 'Effective from is required.' },
              ],
            },
          ],
          interactions: [],
        }}
      />
    </div>
  );
};

export default PricingRateForm;
